import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import NavBar from 'react-bootstrap/Navbar';

import appLogo from '../../assets/img/sidebar-logo.svg';
import collapsedLogo from '../../assets/img/sidebar-collapsed-logo.svg';
import { useMedia } from 'react-use';

import Menu from './SideBarMenu';
import ProfileMenu from './ProfileMenu';
import WorkShiftInfoPanel from './WorkShiftInfoPanel';
import * as URL from '../../lib/router/url';
import { Icon } from '../../modules/core';

// все url-листья меню (с учётом подгрупп) — чтобы отличить страницу-список от «внутренней»
const collectLeafUrls = () => {
  const urls: string[] = [];
  URL.NAVI.forEach(item => {
    if (!Array.isArray(item.kids)) {
      if (item.url && item.url !== 'EMPTY') { urls.push(item.url.split('?')[0]); }
      return;
    }
    item.kids.forEach(k => {
      (Array.isArray(k.kids) ? k.kids : [k]).forEach(leaf => {
        if (leaf.url && leaf.url !== 'EMPTY') { urls.push(leaf.url.split('?')[0]); }
      });
    });
  });
  return urls;
};

// Ручной выбор пользователя (клик по бургеру) — главнее авто-сворачивания; живёт между сессиями
const SIDEBAR_COLLAPSED_KEY = 'sidebarCollapsed';
const readSavedCollapsed = (): boolean | null => {
  try {
    const v = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return v === null ? null : v === '1';
  } catch {
    return null;
  }
};

interface SideBarProps {
  /** Профиль из Redux (state.auth.profile): логотип партнёра. */
  user?: any;
}

const SideBar = ({ user }: SideBarProps) => {
  const logo = user?.partner?.logoUrl || appLogo;

  const isTabMobile = useMedia('(max-width: 991px)');
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => readSavedCollapsed() ?? false);
  const [peek, setPeek] = useState(false); // временное раскрытие-оверлей при наведении на свёрнутую панель

  // авто-сворачивание на «внутренних» страницах (просмотр/редактирование сущности),
  // чтобы рабочее пространство было шире. Работает только пока пользователь
  // не зафиксировал состояние сам (клик по бургеру) — ручной выбор главнее.
  useEffect(() => {
    if (isTabMobile) { return; }
    if (readSavedCollapsed() !== null) { return; }
    const leaves = collectLeafUrls();
    const path = location.pathname;
    const isMenuPage = leaves.some(u => path === u || path === `${ u }/`);
    const isDeep = leaves.some(u => path.startsWith(`${ u }/`)) || /\/\d+(\/|$)/.test(path);
    setPeek(false);
    setCollapsed(!isMenuPage && isDeep);
  }, [location.pathname, isTabMobile]);

  // Клик по бургеру: закрепляем состояние и запоминаем выбор
  const toggleSidebar = () => {
    setPeek(false);
    setCollapsed(c => {
      const next = !c;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* приватный режим */ }
      return next;
    });
  };

  // Состояние прокидываем в body — на него реагирует отступ контента.
  // peek на класс НЕ влияет: при наведении панель раскрывается оверлеем
  // поверх контента (класс sidebar-peek на самой панели), контент не дёргается.
  useEffect(() => {
    const cls = 'sidebar-collapsed';
    if (collapsed && !isTabMobile) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [collapsed, isTabMobile]);

  return (
    <>
      <nav
        className={ `sidebar-container${ collapsed && peek && !isTabMobile ? ' sidebar-peek' : '' }` }
        onMouseEnter={ () => setPeek(true) }
        onMouseLeave={ () => setPeek(false) }
      >
        <NavBar variant='dark' collapseOnSelect expand='lg'>
          <div className='navbar-header'>
            { /* бургер: закрепляет свёрнутое/развёрнутое состояние (только desktop);
                 в развёрнутом виде стоит слева от логотипа в одной строке */ }
            { !isTabMobile && (
              <button
                type='button'
                className='sidebar-burger'
                onClick={ toggleSidebar }
                aria-label={ collapsed ? 'Развернуть меню' : 'Свернуть меню' }
                title={ collapsed ? 'Развернуть меню' : 'Свернуть меню' }
              >
                <Icon name='bars' />
              </button>
            ) }

            <NavBar.Brand id='app-nav-bar'>
              <Link to='/'>
                <img src={ logo } alt='logo' className='top-logo' />
                { /* иконка-замена логотипа в свёрнутом состоянии (видна только при .sidebar-collapsed) */ }
                <img src={ collapsedLogo } alt='' className='collapsed-logo' />
              </Link>
            </NavBar.Brand>

            { isTabMobile && <WorkShiftInfoPanel /> }

            <ProfileMenu minimal mobile />
            <NavBar.Toggle aria-controls='app-nav-bar' />
          </div>

          <NavBar.Collapse id='app-nav-bar'>
            <Menu />
          </NavBar.Collapse>
        </NavBar>
      </nav>

    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    user: state.auth?.profile,
  };
};

export default connect(mapStateToProps)(SideBar);
